import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useMemo, useCallback, useState } from "react";
import { useWidgetConfig, customColor, useStudioVoiceSkills, useStudioVoiceBranding } from "@brandclub/bc-widgets";
import { useConciergeContext } from "../../ConciergeContextProvider.js";
import { PromptInputConcierge } from "../PromptInputConcierge/index.js";
import { useSupportCopy } from "../SupportThemeProvider.js";
const DEFAULT_PLACEHOLDER = "Ask me anything";
const DEFAULT_FOLLOW_UP_PLACEHOLDER = "Have another question?";
const ConciergeInputArea = ({
  searchInputValue,
  setSearchInputValue,
  handleSearch,
  isActivelyStreaming,
  conversationHistoryLength,
  onTrackAccessPoint,
  onStop,
  widgetId = "concierge-panel-chat-input",
  displayName = "Chat Input (Conversation)",
  initialState,
  presets,
  forceMobile = false,
  liveTakeoverActive = false,
  adminName,
  adminAvatarUrl,
  onEndLiveAgent
}) => {
  const chatInputConfig = useWidgetConfig(widgetId, {});
  const homeInputConfig = useWidgetConfig("concierge-chat-input", {});
  const supportCopy = useSupportCopy();
  const adminFirstName = useMemo(() => adminName ? adminName.split(" ")[0] : void 0, [adminName]);
  const placeholder = useMemo(() => {
    if (liveTakeoverActive) {
      if (adminFirstName) {
        return supportCopy.takeoverPlaceholder.replace(/\{firstName\}/g, adminFirstName);
      }
      return "Reply to support…";
    }
    if (conversationHistoryLength > 0) {
      return chatInputConfig.placeholderFollowUp || DEFAULT_FOLLOW_UP_PLACEHOLDER;
    }
    return chatInputConfig.placeholderDefault || DEFAULT_PLACEHOLDER;
  }, [
    liveTakeoverActive,
    adminFirstName,
    conversationHistoryLength,
    chatInputConfig.placeholderDefault,
    chatInputConfig.placeholderFollowUp,
    supportCopy.takeoverPlaceholder
  ]);
  const handleSearchWithTracking = useCallback(
    (text, files) => {
      onTrackAccessPoint == null ? void 0 : onTrackAccessPoint("search_bar", text);
      handleSearch(text, files);
    },
    [handleSearch, onTrackAccessPoint]
  );
  const mergedInitialState = useMemo(() => {
    const base = {
      ...homeInputConfig.fontFamily ? { fontFamily: homeInputConfig.fontFamily } : {},
      ...initialState
    };
    if (liveTakeoverActive) {
      return {
        ...base,
        borderColor: customColor("transparent"),
        borderColorFocus: customColor("transparent"),
        borderWidth: 0,
        shadowEnabled: false
      };
    }
    return base;
  }, [homeInputConfig.fontFamily, initialState, liveTakeoverActive]);
  const [endPending, setEndPending] = useState(false);
  const handleEnd = useCallback(async () => {
    if (!onEndLiveAgent || endPending) return;
    setEndPending(true);
    try {
      await onEndLiveAgent();
    } finally {
      setEndPending(false);
    }
  }, [onEndLiveAgent, endPending]);
  const voiceSkills = useStudioVoiceSkills();
  const voiceBranding = useStudioVoiceBranding();
  const { conversationId, openVoiceCall } = useConciergeContext();
  const showVoiceCall = voiceSkills.enabled && !liveTakeoverActive;
  const handleVoiceCallClick = useCallback(() => {
    openVoiceCall(conversationId ?? void 0);
  }, [openVoiceCall, conversationId]);
  const promptInput = /* @__PURE__ */ jsx(
    PromptInputConcierge,
    {
      placeholder,
      value: searchInputValue,
      onChange: setSearchInputValue,
      onSubmit: handleSearchWithTracking,
      isStreaming: isActivelyStreaming,
      onStop,
      widgetId,
      displayName,
      presets,
      forceMobile,
      initialState: mergedInitialState,
      showCallButton: showVoiceCall,
      onCallClick: showVoiceCall ? handleVoiceCallClick : void 0,
      voiceAssistantName: voiceBranding.persona.assistantName
    }
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "tw:flex tw:shrink-0 tw:flex-col tw:gap-1 tw:px-4 tw:pt-1",
      style: { paddingBottom: "max(1rem, env(safe-area-inset-bottom))" },
      children: liveTakeoverActive ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            role: "status",
            "aria-live": "polite",
            className: "tw:mx-auto tw:w-full tw:max-w-[768px] tw:overflow-hidden tw:rounded-2xl tw:border tw:bg-white tw:shadow-sm",
            style: { borderColor: "var(--bc-support-border)" },
            children: [
              /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "tw:flex tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:px-3 tw:py-2",
                  style: {
                    backgroundColor: "var(--bc-support-bg)",
                    borderBottomColor: "var(--bc-support-border)"
                  },
                  children: [
                    /* @__PURE__ */ jsxs(
                      "span",
                      {
                        className: "tw:flex tw:min-w-0 tw:items-center tw:gap-2 tw:text-sm tw:truncate",
                        style: { color: "var(--bc-support-text)" },
                        children: [
                          /* @__PURE__ */ jsxs("span", { className: "tw:relative tw:flex tw:h-1.5 tw:w-1.5 tw:shrink-0", children: [
                            /* @__PURE__ */ jsx(
                              "span",
                              {
                                className: "tw:absolute tw:inline-flex tw:h-full tw:w-full tw:animate-ping tw:rounded-full tw:opacity-75",
                                style: { backgroundColor: "var(--bc-support-dot)" }
                              }
                            ),
                            /* @__PURE__ */ jsx(
                              "span",
                              {
                                className: "tw:relative tw:inline-flex tw:h-1.5 tw:w-1.5 tw:rounded-full",
                                style: { backgroundColor: "var(--bc-support-dot)" }
                              }
                            )
                          ] }),
                          adminName ? /* @__PURE__ */ jsxs("span", { className: "tw:truncate", children: [
                            "Connected to ",
                            /* @__PURE__ */ jsx("span", { className: "tw:font-semibold", children: adminName }),
                            /* @__PURE__ */ jsx("span", { className: "tw:mx-1", children: "·" }),
                            /* @__PURE__ */ jsx("span", { children: supportCopy.teamLabel })
                          ] }) : /* @__PURE__ */ jsxs("span", { className: "tw:truncate", children: [
                            "Connected to ",
                            /* @__PURE__ */ jsx("span", { className: "tw:font-semibold", children: supportCopy.teamLabel })
                          ] })
                        ]
                      }
                    ),
                    onEndLiveAgent && /* @__PURE__ */ jsx(
                      "button",
                      {
                        type: "button",
                        onClick: handleEnd,
                        disabled: endPending,
                        className: "tw:shrink-0 tw:inline-flex tw:cursor-pointer tw:items-center tw:rounded-full tw:border tw:px-3 tw:py-1 tw:text-xs tw:font-semibold tw:transition-colors hover:tw:border-red-300 hover:tw:bg-red-50 hover:tw:text-red-600 disabled:tw:cursor-not-allowed disabled:tw:opacity-50",
                        style: {
                          borderColor: "var(--bc-support-border)",
                          backgroundColor: "var(--bc-support-bg)",
                          color: "var(--bc-support-text)"
                        },
                        "aria-label": `Disconnect from ${supportCopy.teamLabel.toLowerCase()}`,
                        children: endPending ? "Ending…" : "Disconnect"
                      }
                    )
                  ]
                }
              ),
              promptInput
            ]
          }
        ),
        /* @__PURE__ */ jsx("p", { className: "tw:mx-auto tw:mt-1 tw:max-w-[768px] tw:text-center tw:text-xs tw:text-gray-500", children: supportCopy.pausedFooterCopy })
      ] }) : promptInput
    }
  );
};
export {
  ConciergeInputArea
};
//# sourceMappingURL=ConciergeInputArea.js.map
